import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../redux/thunks/authThunks';
import { fetchUserBalance } from '../../redux/thunks/marketplaceThunks';
import { FaCog, FaWallet, FaSignOutAlt, FaChevronDown, FaMoon, FaSun, FaUserShield, FaChartLine, FaRobot } from 'react-icons/fa';
import { User } from '../../redux/slices/authSlice';
import { AppDispatch, RootState } from '../../redux/Store';
import defaultAvatar from '../../images/default_avatar.png';
import { getMediaUrl } from '@/constants/media';
import './UserMenu.scss';

interface UserMenuProps {
  user: User | null;
  isDarkMode?: boolean;
  onThemeToggle?: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ user, isDarkMode = true, onThemeToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // Both balance and walletAddress come from /marketplace/balance, which
  // works for wallet AND email users. Dispatching fetchUserBalance also
  // fills walletAddress as a side-effect — important because the
  // previous version assumed walletAddress was already populated and
  // gated the balance fetch behind it. If you hadn't visited a page
  // that dispatched fetchUserBalance, walletAddress stayed null and no
  // balance fetch ever fired — the UI sat at "—" / "Loading…" forever.
  const { walletAddress, userBalance } = useSelector((state: RootState) => state.marketplace);

  // Fetch on mount (handles the "loaded straight into a page that
  // doesn't dispatch fetchUserBalance" case — e.g. /post/:id, /chat).
  useEffect(() => {
    if (user) dispatch(fetchUserBalance());
  }, [user, dispatch]);

  // Refresh on menu open so a recent purchase/spend is reflected when
  // the user actually looks at the number. Skips the noop if isOpen
  // is false.
  useEffect(() => {
    if (user && isOpen) dispatch(fetchUserBalance());
  }, [isOpen, user, dispatch]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  // Clicking the avatar (in the trigger OR inside the dropdown header)
  // navigates straight to My Profile — the menu no longer holds a
  // "My Profile" item; Analytics takes its slot. stopPropagation so
  // the trigger button doesn't also open/close the dropdown on
  // avatar click. Closes the dropdown if it happened to be open.
  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    if (user?.username) {
      navigate(`/profile/@${user.username}`);
    }
  };

  const handleWalletClick = () => {
    if (walletAddress) {
      const network = import.meta.env.VITE_KLEVER_NETWORK || 'testnet';
      const scanBase = network === 'mainnet'
        ? 'https://kleverscan.org'
        : 'https://testnet.kleverscan.org';
      window.open(`${scanBase}/account/${walletAddress}`, '_blank');
    }
    setIsOpen(false);
  };

  // Get display name
  const getDisplayName = (): string => {
    if (!user) return 'User';
    return user.username || user.email?.split('@')[0] || 'User';
  };

  // Shorten wallet address
  const shortenAddress = (address: string): string => {
    if (address.length <= 13) return address;
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  if (!user) return null;

  return (
    <div className="user-menu" ref={menuRef}>
      {/* User Button */}
      <button
        className="user-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Avatar — clickable shortcut to My Profile. Wrapped in a
            span with role=link (rather than nesting a button/link
            inside the trigger button) to keep DOM valid. */}
        <span
          className="user-avatar user-avatar--clickable"
          role="link"
          tabIndex={0}
          onClick={handleAvatarClick}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAvatarClick(e as any); }}
          title="Go to my profile"
        >
          <img src={getMediaUrl(user?.avatarUrl || null) || defaultAvatar} alt={getDisplayName()} />
        </span>

        {/* Username (Desktop) */}
        <span className="user-name desktop-only">
          Welcome, {getDisplayName()}
        </span>

        {/* Dropdown Icon */}
        <FaChevronDown className={`user-menu-icon ${isOpen ? 'open' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="user-menu-dropdown">
          {/* User Info Section — avatar + display name click through
              to My Profile, matching the trigger-avatar behaviour. */}
          <div className="user-menu-header">
            <div
              className="user-avatar-large user-avatar-large--clickable"
              role="link"
              tabIndex={0}
              onClick={handleAvatarClick}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleAvatarClick(e as any); }}
              title="Go to my profile"
            >
              <img src={getMediaUrl(user?.avatarUrl || null) || defaultAvatar} alt={getDisplayName()} />
            </div>
            <div className="user-info">
              <p className="user-display-name">{getDisplayName()}</p>
              <p className="user-email">{user.email}</p>
            </div>
          </div>

          {/* Wallet Section */}
          <div className="user-menu-wallet">
            <div className="wallet-info">
              <span className="wallet-label">KLV Balance</span>
              <span className="wallet-balance">
                {typeof userBalance === 'number'
                  ? userBalance.toFixed(2)
                  : '—'} KLV
              </span>
            </div>
            <button
              className="wallet-address-btn"
              onClick={handleWalletClick}
              title="View on KleverScan"
            >
              <FaWallet />
              <span>
                {walletAddress
                  ? shortenAddress(walletAddress)
                  : 'Loading...'}
              </span>
            </button>
          </div>

          <div className="user-menu-divider" />

          {/* Theme Toggle */}
          <div className="user-menu-theme">
            <div className="theme-toggle-row">
              <div className="theme-toggle-label">
                {isDarkMode ? <FaMoon /> : <FaSun />}
                <span>{isDarkMode ? 'Dark Mode' : 'Light Mode'}</span>
              </div>
              <button
                className={`theme-toggle-switch ${isDarkMode ? 'active' : ''}`}
                onClick={onThemeToggle}
                aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
              >
                <span className="theme-toggle-slider" />
              </button>
            </div>
          </div>

          <div className="user-menu-divider" />

          {/* Menu Items — "My Profile" moved onto the avatar itself
              (single click on the avatar navigates to /profile/@me),
              freeing this slot for Analytics. Analytics was previously
              only reachable by clicking stat cards; issue #529 wanted
              a first-class entry that isn't in the (already long)
              hamburger. */}
          <ul className="user-menu-items">
            <li>
              <button
                className="user-menu-item"
                onClick={() => handleNavigate('/analytics')}
              >
                <FaChartLine />
                <span>Analytics</span>
              </button>
            </li>
            <li>
              <button
                className="user-menu-item"
                onClick={() => handleNavigate('/settings')}
              >
                <FaCog />
                <span>Settings</span>
              </button>
            </li>
            <li>
              <button
                className="user-menu-item"
                onClick={handleWalletClick}
                title="View on KleverScan"
              >
                <FaWallet />
                <span>Wallet</span>
              </button>
            </li>
            {/* Agents menu — trimmed to just "My Agents" (per user
                feedback, previously bloated with three entries). The
                register + discover surfaces are reachable from the
                My Agents page's action row, keeping the user menu
                focused on the caller's own things. Public discover
                also gets a top-level nav item so it's not hidden
                behind the user menu. */}
            <li>
              <button
                className="user-menu-item"
                onClick={() => handleNavigate('/agents/mine')}
              >
                <FaRobot />
                <span>My Agents</span>
              </button>
            </li>
          </ul>

          {/* Admin Panel Section - Only visible to admins */}
          {user.role === 'admin' && (
            <>
              <div className="user-menu-divider" />

              <ul className="user-menu-items admin-items">
                <li>
                  <button
                    className="user-menu-item admin-item"
                    onClick={() => handleNavigate('/admin/dashboard')}
                  >
                    <FaUserShield />
                    <span>Admin Dashboard</span>
                  </button>
                </li>
              </ul>
            </>
          )}

          <div className="user-menu-divider" />

          {/* Logout */}
          <button className="user-menu-logout" onClick={handleLogout}>
            <FaSignOutAlt />
            <span>Log Out</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
